import React from 'react';
import { Link } from 'react-router-dom';
import { Container } from 'react-bootstrap';
import { FaExclamationTriangle, FaMapMarkerAlt, FaGlobeAsia, FaFileSignature } from 'react-icons/fa';
import { ScrollText, Reveal } from '../../../components/motion';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/ui/Icon';
import Eyebrow from '../../../components/ui/Eyebrow';
import logoUrl from '../../../assets/images/logo.svg';
import FaqList from '../../../components/ui/FaqList';
import usePageMeta from '../../../hooks/usePageMeta';
import { COUNTRIES, buildJsonLd, pageUrl } from '../content/shared';
import type { ServiceContent, ServiceSection } from '../content/types';
import styles from './ServicePage.module.css';

/** Section number shown as a big outlined "02" beside each heading. */
const pad = (n: number) => String(n).padStart(2, '0');

const SectionHead: React.FC<{ num: number; children: React.ReactNode }> = ({ num, children }) => (
  <Reveal className={styles.secHead}>
    <span className={styles.secNum} aria-hidden="true">
      {pad(num)}
    </span>
    <h2 className={styles.h2}>{children}</h2>
  </Reveal>
);

const Section: React.FC<{ section: ServiceSection; num: number }> = ({ section: s, num }) => (
  <div className={styles.block}>
    <SectionHead num={num}>{s.heading}</SectionHead>

    {s.paragraphs?.map((p, i) => (
      <Reveal as="p" key={i} className={styles.text} delay={0.05}>
        {p}
      </Reveal>
    ))}

    {s.cards && (
      <div className={styles.modes}>
        {s.cards.map((c, i) => (
          <Reveal key={c.title} className={styles.modeCard} delay={0.1 + i * 0.08}>
            <span className={styles.modeIcon}>
              <Icon icon={c.icon} aria-hidden />
            </span>
            <h3 className={styles.h3}>{c.title}</h3>
            <p className={styles.text}>{c.text}</p>
            {c.link && (
              <Link to={c.link.to} className={styles.cardLink}>
                {c.link.label} →
              </Link>
            )}
          </Reveal>
        ))}
      </div>
    )}

    {s.steps && (
      <Reveal delay={0.05}>
        <ol className={styles.steps}>
          {s.steps.map((step, i) => (
            <li key={step.title} className={styles.step}>
              <span className={styles.stepNum}>{i + 1}</span>
              <div>
                <p className={styles.stepTitle}>{step.title}</p>
                <p className={styles.stepText}>{step.text}</p>
              </div>
            </li>
          ))}
        </ol>
      </Reveal>
    )}

    {s.bullets && (
      <Reveal delay={0.05}>
        <ul className={styles.bullets}>
          {s.bullets.map((b) => (
            <li key={b}>{b}</li>
          ))}
        </ul>
      </Reveal>
    )}

    {s.chips && (
      <Reveal delay={0.05}>
        <ul className={styles.chipList}>
          {s.chips.map((c) => (
            <li key={c} className={styles.chip}>
              <span className={styles.chipDot} aria-hidden="true" />
              {c}
            </li>
          ))}
        </ul>
      </Reveal>
    )}

    {s.countries && (
      <Reveal delay={0.1}>
        <ul className={styles.countries}>
          {COUNTRIES.map((c) => (
            <li key={c.code} className={styles.country}>
              <img
                src={`https://flagcdn.com/w40/${c.code}.png`}
                srcSet={`https://flagcdn.com/w80/${c.code}.png 2x`}
                alt=""
                width={24}
                height={16}
                loading="lazy"
              />
              {c.name}
            </li>
          ))}
        </ul>
      </Reveal>
    )}

    {s.warning && (
      <Reveal className={`${styles.warning} ${styles.warningWrap}`} delay={0.1}>
        <Icon icon={FaExclamationTriangle} className={styles.warningIcon} aria-hidden />
        <p>{s.warning}</p>
      </Reveal>
    )}
  </div>
);

/** Fact strip under the banner — true for every service. */
const QUICK_FACTS = [
  { icon: FaMapMarkerAlt, value: '12', label: 'Branches in KSA' },
  { icon: FaGlobeAsia, value: '10', label: 'Countries' },
  { icon: FaFileSignature, value: 'Customs', label: 'Handled by us' },
];

/** Shared layout for every /services/:slug page; all copy comes from content/*.ts. */
const ServicePage: React.FC<{ content: ServiceContent }> = ({ content }) => {
  usePageMeta({
    title: content.meta.title,
    description: content.meta.description,
    url: pageUrl(content.slug),
  });

  return (
    <section className={styles.page}>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(buildJsonLd(content)) }}
      />
      <Container fluid="xl" className={styles.inner}>
        {/* Photo banner: breadcrumb, label, title and buttons over a dark-red shade. */}
        <div className={styles.banner}>
          <img src={content.heroImage} alt={content.heroAlt} className={styles.bannerImage} />
          <span className={styles.badge}>
            <img src={logoUrl} alt="Sahari Cargo" />
          </span>
          <div className={styles.bannerText}>
            <p className={styles.crumb}>
              <Link to="/">Home</Link> › Services › {content.breadcrumbName}
            </p>
            <Eyebrow light>Our services</Eyebrow>
            <ScrollText as="h1" className={styles.title} mode="enter">
              {content.h1}
            </ScrollText>
            <Reveal className={styles.actions} delay={0.15}>
              <Button to="/book-shipment" variant="primary">Get a Quote</Button>
              <Button to="/tracking" variant="outline" className={styles.ghostBtn}>
                Track Shipment
              </Button>
            </Reveal>
          </div>
        </div>

        {/* White fact strip overlapping the photo's bottom edge. */}
        <ul className={styles.facts}>
          {QUICK_FACTS.map((f) => (
            <li key={f.label}>
              <span className={styles.factIcon} aria-hidden="true">
                <Icon icon={f.icon} />
              </span>
              <span>
                <strong>{f.value}</strong>
                <span>{f.label}</span>
              </span>
            </li>
          ))}
        </ul>

        <div className={styles.column}>
          <div className={styles.intro}>
            {content.intro.map((p, i) => (
              <Reveal as="p" key={i} className={styles.lead} delay={0.1}>
                {p}
              </Reveal>
            ))}
          </div>

          {content.sections.map((s, i) => (
            <Section key={s.heading} section={s} num={i + 1} />
          ))}

          <div className={styles.block}>
            <SectionHead num={content.sections.length + 1}>Frequently Asked Questions</SectionHead>
            <FaqList faqs={content.faqs} idPrefix="service-faq" />
          </div>

          <Reveal className={styles.cta}>
            <div>
              <p className={styles.ctaTitle}>{content.cta.title}</p>
              <p className={styles.ctaText}>{content.cta.text}</p>
            </div>
            <div className={styles.ctaActions}>
              <Button to="/book-shipment" variant="outline" className={styles.ctaPrimary}>
                Get a Quote
              </Button>
              <Button to="/tracking" variant="outline" className={styles.ctaGhost}>
                Track Shipment
              </Button>
            </div>
          </Reveal>
        </div>
      </Container>
    </section>
  );
};

export default ServicePage;
